import { Card } from "@design";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import type { FlowResponse } from "@/api";
import { listFlowRunsOptions } from "@/api";
import { Text } from "@/components/Text";
import { formatDateTime } from "@/i18n/format";
import { FlowStatusBadge } from "./flowMeta";
import { flowRunPath } from "./flowRunPaths";
import { RunFlowButton } from "./RunFlowButton";

/** Runs tab: run history (click a run to open it) and Run. */
export function FlowRunsTab({
  flow,
  runPath = flowRunPath,
  showRun = true,
}: Readonly<{
  flow: FlowResponse;
  /** Where a run's page lives (the Build area by default). */
  runPath?: (flowUuid: string, runUuid: string) => string;
  /** Show Run here (off where the page header already has it). */
  showRun?: boolean;
}>) {
  const { t } = useTranslation("workflows");
  const navigate = useNavigate();
  const runs = useQuery(listFlowRunsOptions({ path: { flow_uuid: flow.uuid }, query: { page_size: 50 } }));
  const items = runs.data?.data ?? [];

  return (
    <Card>
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <Text variant="body">{t("runs.title")}</Text>
          {showRun && items.length > 0 && <RunFlowButton flow={flow} variant="secondary" runPath={runPath} />}
        </div>
        {items.length > 0 ? (
          <ul className="flex flex-col divide-y divide-border-subtle">
            {items.map((run) => (
              <li key={run.uuid}>
                <button
                  type="button"
                  onClick={() => navigate(runPath(flow.uuid, run.uuid))}
                  className="flex w-full cursor-pointer items-center gap-3 rounded py-2 text-left btn-focus-ring"
                >
                  <FlowStatusBadge status={run.status} />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <Text variant="body-sm" className="truncate">
                      {formatDateTime(run.startedAt ?? run.createdAt)}
                    </Text>
                    <Text variant="caption" tone="tertiary">
                      {t("runs.tokens", { count: run.totalTokens })}
                    </Text>
                  </div>
                  <Text variant="caption" tone="tertiary">
                    {t("runs.viewRun")}
                  </Text>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="flex flex-col items-start gap-3">
            <Text variant="body-sm" tone="tertiary">
              {t("runs.emptyRunNow")}
            </Text>
            {showRun && <RunFlowButton flow={flow} runPath={runPath} />}
          </div>
        )}
      </div>
    </Card>
  );
}
